import { FC, ReactNode } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import styles from './BottomBar.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Small line on the left of the actions, e.g. "3 of 7 reviewed" */
  label?: ReactNode
  /** The figure under the label, e.g. "4 left" */
  value?: ReactNode
  /** The actions (compose `MUIButton`s); they share the width left by the summary */
  actions: ReactNode
  /** Hidden from the `sm` breakpoint up, for the phone's counterpart of an action shown in the page */
  isPhoneOnly?: boolean
  /** Pinned to the bottom of the viewport instead of sticking to the bottom of its scroller */
  isFixed?: boolean
  /** Name of the region, so screen-reader users can jump to it */
  'aria-label'?: string
}

/**
 * @component The phone's action bar: full width, at the bottom of the screen (or of its scroller), clear of the home
 * indicator, with an optional summary on the left and the actions sharing the rest. A named region.
 * A fixed bar inside a transformed ancestor scrolls with it: portal it to `document.body` then.
 * @example
 * <BottomBar isPhoneOnly isFixed aria-label={t('start')} actions={<MUIButton size="lg">{t('start')}</MUIButton>} />
 */
export const BottomBar: FC<Props> = ({
  label,
  value,
  actions,
  isPhoneOnly = false,
  isFixed = false,
  'aria-label': ariaLabel,
}) => (
  <div
    role="region"
    aria-label={ariaLabel}
    className={classnames(styles.bottomBar, { [styles.fixed]: isFixed, [styles.phoneOnly]: isPhoneOnly })}
  >

    {(label != null || value != null) &&
      <div className={styles.summary}>

        {label != null && <Typography variant="text-xs" color={GRAY_700}>{label}</Typography>}

        {value != null && <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>{value}</Typography>}

      </div>
    }

    <div className={styles.actions}>
      {actions}
    </div>

  </div>
)
